<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const DEFAULT_LABEL = 'sla-estourado';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const minutes = ref(null);
const breachLabel = ref(DEFAULT_LABEL);
const isEnabled = ref(false);
const isSubmitting = ref(false);

watch(
  currentAccount,
  () => {
    const { sla_first_response_minutes, sla_breach_label } =
      currentAccount.value?.settings || {};

    minutes.value = sla_first_response_minutes;
    breachLabel.value = sla_breach_label || DEFAULT_LABEL;
    isEnabled.value = Boolean(sla_first_response_minutes);
  },
  { deep: true, immediate: true }
);

const updateSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.FIRST_RESPONSE_TARGET.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.FIRST_RESPONSE_TARGET.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (!minutes.value || minutes.value < 1) {
    useAlert(t('GENERAL_SETTINGS.FORM.FIRST_RESPONSE_TARGET.MINUTES.ERROR'));
    return Promise.resolve();
  }

  return updateSettings({
    sla_first_response_minutes: Number(minutes.value),
    sla_breach_label: breachLabel.value || DEFAULT_LABEL,
  });
};

const toggleTarget = async () => {
  if (isEnabled.value) return;

  minutes.value = null;
  await updateSettings({ sla_first_response_minutes: null });
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <div class="flex justify-between items-center w-full">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ t('GENERAL_SETTINGS.FORM.FIRST_RESPONSE_TARGET.TITLE') }}
        </h3>
        <div class="flex justify-end">
          <Switch v-model="isEnabled" @change="toggleTarget" />
        </div>
      </div>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.FIRST_RESPONSE_TARGET.NOTE') }}
      </p>
    </div>

    <div v-if="isEnabled" class="px-5 py-4">
      <form class="grid gap-5" @submit.prevent="handleSubmit">
        <WithLabel
          :label="
            t('GENERAL_SETTINGS.FORM.FIRST_RESPONSE_TARGET.MINUTES.LABEL')
          "
          :help-message="
            t('GENERAL_SETTINGS.FORM.FIRST_RESPONSE_TARGET.MINUTES.HELP')
          "
        >
          <input
            v-model="minutes"
            type="number"
            min="1"
            max="1440"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          :label="t('GENERAL_SETTINGS.FORM.FIRST_RESPONSE_TARGET.LABEL.LABEL')"
          :help-message="
            t('GENERAL_SETTINGS.FORM.FIRST_RESPONSE_TARGET.LABEL.HELP')
          "
        >
          <input v-model="breachLabel" type="text" class="w-full" />
        </WithLabel>
        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="
              t('GENERAL_SETTINGS.FORM.FIRST_RESPONSE_TARGET.UPDATE_BUTTON')
            "
          />
        </div>
      </form>
    </div>
  </div>
</template>
